import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ApiService from 'redux/services/api-service';
import {addResultCases} from 'utils/helpers';
import {CampaignTask, CampaignTaskStatus, CampaignTaskStatusObj, ListResult} from 'utils/types';
import {storeEndpoints} from '../store/slice';
import {navigationActions} from '../navigation/slice';

export interface IState {
  /** @warn DO NOT DEP-ARRAY. Reference must over-update because 1) it will become "pending"/<empty-array> at interval 2) inner tasks re-reference the array. */
  tasks: ListResult<CampaignTask>;
  runAnalysisInProgress: boolean;
}

type CampaignTaskListPayload = CampaignTask & {
  status_data?: CampaignTaskStatusObj;
};

type CampaignTasksResponse = {
  count: number;
  results: CampaignTaskListPayload[];
};

type MutableTasksResult = ListResult<CampaignTask> & {
  count?: number;
  requestId?: string;
  results?: CampaignTask[];
};
const ALT_PROG_STATES = {
  SUCCESS: 'COMPLETED',
  FAILURE: 'FAILED',
  REVOKED: 'TERMINATED',
};
const getTaskStatusFromProgressState = (state?: string): CampaignTaskStatus | null | undefined => {
  return state && state in ALT_PROG_STATES ? ALT_PROG_STATES[state as any] : (state as CampaignTaskStatus);
};

const normalizeCampaignTask = (task: CampaignTaskListPayload): CampaignTask => {
  const {status_data, ...taskData} = task;
  const taskStatus = taskData.taskStatus || status_data;
  const normalizedTask: CampaignTask = {
    ...taskData,
    taskStatus,
  };

  const mappedStatus = getTaskStatusFromProgressState(taskStatus?.state);
  if (mappedStatus) normalizedTask.status = mappedStatus;

  return normalizedTask;
};

const ensureTaskResults = (state: IState): CampaignTask[] => {
  const tasksState = state.tasks as MutableTasksResult;

  if (!Array.isArray(tasksState.results)) tasksState.results = [];

  if (typeof tasksState.count !== 'number') tasksState.count = tasksState.results.length;

  if (tasksState.as === 'idle' || tasksState.as === 'error') {
    state.tasks = {
      as: 'success',
      count: tasksState.results.length,
      results: tasksState.results,
    };
    return state.tasks.results;
  }

  return tasksState.results;
};

const appendTask = (state: IState, task: CampaignTask) => {
  const tasks = ensureTaskResults(state);
  tasks.push(task);
  (state.tasks as MutableTasksResult).count = tasks.length;
};

const updateTaskStatus = (state: IState, celeryTaskId: number, taskStatus: CampaignTaskStatusObj) => {
  const tasks = ensureTaskResults(state);
  const task = tasks.find(item => {
    if (item.task_id && String(item.task_id) === String(celeryTaskId)) return true;

    return item.data?.celery_task_id && String(item.data.celery_task_id) === String(celeryTaskId);
  });

  if (!task) return;

  task.taskStatus = taskStatus;

  const mappedStatus = getTaskStatusFromProgressState(taskStatus.state);
  if (mappedStatus) task.status = mappedStatus;
};

export const campaignTaskEndpoints = {
  getCampaignTasks: createAsyncThunk('campaignTask/getCampaignTasks', async (campaignId: any) => {
    const response = await ApiService.get<CampaignTasksResponse>(
      `/campaign_task/?campaign_id=${campaignId}&ordering=-id`,
    );
    response.data.results = response.data.results.map(normalizeCampaignTask);
    return response.data;
  }),
  createCampaignTask: createAsyncThunk('campaignTask/createCampaignTask', async (payload: any) => {
    const response = await ApiService.post('/campaign_task/', payload);
    return response.data;
  }),
  runAnalysis: createAsyncThunk('campaignTask/runAnalysis', async (payload: any, {rejectWithValue}) => {
    try {
      const response = await ApiService.post('/run_campaign_analysis/', payload);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }),
  runAreaAnalysis: createAsyncThunk('campaignTask/runAnalysis', async (payload: any, {rejectWithValue}) => {
    try {
      const response = await ApiService.post('/run_area_campaign_analysis/', payload);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }),
  runAnalysisBatch: createAsyncThunk('campaignTask/runAnalysisBatch', async (payload: any, {rejectWithValue}) => {
    try {
      const response = await ApiService.post('/run_campaign_analysis_batch/', payload);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }),
  runAreaAnalysisBatch: createAsyncThunk('campaignTask/runAnalysisBatch', async (payload: any, {rejectWithValue}) => {
    try {
      const response = await ApiService.post('/run_area_campaign_analysis_batch/', payload);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }),
  validateRunAnalysis: createAsyncThunk(
    'campaignTask/runAnalysis',
    async (campaignId: number | string, {rejectWithValue}) => {
      try {
        const response = await ApiService.get(`/run_campaign_analysis/?campaign_id=${campaignId}`);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response);
      }
    },
  ),
  validateRunAreaAnalysis: createAsyncThunk(
    'campaignTask/runAnalysis',
    async (campaignId: number | string, {rejectWithValue}) => {
      try {
        const response = await ApiService.get(`/run_area_campaign_analysis/?campaign_id=${campaignId}`);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response);
      }
    },
  ),
  runMultivariateAnalysis: createAsyncThunk(
    'campaignTask/runMultivariateAnalysis',
    async (
      payload: {campaign_id: number | string; sales_lift_analysis_run_id: number; report_type: string},
      {rejectWithValue},
    ) => {
      try {
        const response = await ApiService.post('/run_multivariate_analysis/', payload);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response.data);
      }
    },
  ),
  validateRunMultivariateAnalysis: createAsyncThunk(
    'campaignTask/runMultivariateAnalysis',
    async (campaignId: number | string, {rejectWithValue}) => {
      try {
        const response = await ApiService.get(`/run_multivariate_analysis/?campaign_id=${campaignId}`);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response);
      }
    },
  ),
  validateRunAnalysisBatch: createAsyncThunk(
    'campaignTask/runAnalysisBatch',
    async (payload: any, {rejectWithValue}) => {
      try {
        const response = await ApiService.get(
          `/run_campaign_analysis_batch/?campaign_id=${payload.campaignId}&batch_type=${payload.batchType}`,
        );
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response);
      }
    },
  ),
  validateRunAreaAnalysisBatch: createAsyncThunk(
    'campaignTask/runAnalysisBatch',
    async (payload: any, {rejectWithValue}) => {
      try {
        const response = await ApiService.get(
          `/run_area_campaign_analysis_batch/?campaign_id=${payload.campaignId}&batch_type=${payload.batchType}`,
        );
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response);
      }
    },
  ),
  computeSales: createAsyncThunk('campaignTask/computeSales', async (payload: any, {rejectWithValue}) => {
    try {
      const response = await ApiService.post('/run_per_store_sales_calculation/', payload);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }),
  computeItemSales: createAsyncThunk('campaignTask/computeItemSales', async (payload: any, {rejectWithValue}) => {
    try {
      const response = await ApiService.post('/run_per_item_sales_calculation/', payload);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }),
  computeSalesReportData: createAsyncThunk(
    'campaignTask/computeSalesReportData',
    async (payload: any, {rejectWithValue}) => {
      try {
        const response = await ApiService.post('/run_sales_report_data_calculation/', payload);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response.data);
      }
    },
  ),
  computeSalesReportDataRegional: createAsyncThunk(
    'campaignTask/computeSalesReportDataRegional',
    async (payload: any, {rejectWithValue}) => {
      try {
        const response = await ApiService.post('/run_sales_report_data_calculation_regional/', payload);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response.data);
      }
    },
  ),
  computeStoreRollups: createAsyncThunk('campaignTask/computeStoreRollups', async (payload: any, {rejectWithValue}) => {
    try {
      const response = await ApiService.post('/calculate_store_rollup_views/', payload);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }),
  syncCampaignEntitiesFromSales: createAsyncThunk(
    'campaignTask/syncCampaignEntitiesFromSales',
    async (payload: any, {rejectWithValue}) => {
      try {
        const response = await ApiService.post('/run_sync_campaign_entities_from_sales/', payload);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response.data);
      }
    },
  ),
  refreshStoreMetrics: createAsyncThunk('campaignTask/refreshStoreMetrics', async (payload: any) => {
    const response = await ApiService.post('/run_store_metrics/', payload);
    return response.data;
  }),
  refreshSalesMetrics: createAsyncThunk('campaignTask/refreshSalesMetrics', async (payload: any) => {
    const response = await ApiService.post('/run_sales_metrics/', payload);
    return response.data;
  }),
  getCampaignTaskStatus: createAsyncThunk('campaignTask/getCampaignTaskStatus', async (celery_task_id: number) => {
    const response = await ApiService.get(`/task_progress/${celery_task_id}/`);
    return response.data;
  }),
  startValassisReportGeneration: createAsyncThunk(
    'campaign/startValassisReportGeneration',
    async (payload: any, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(
          `/campaign_valassis_report/${payload.id}/${payload.campaign_id}/`,
          payload,
        );
        return response.data;
      } catch (err: any) {
        if (err.response.status === 400 && err.response.data && err.response.data.length > 0) {
          return rejectWithValue(err.response.data[0]);
        }
        return rejectWithValue('Unknown error occurred on the server');
      }
    },
  ),
  importCampaignDataFile: createAsyncThunk(
    'campaignFileUploads/importFile',
    async (payload: any, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(`/campaign_file_upload/${payload.id}/import_file/`, payload);
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response.data);
      }
    },
  ),
  queueCreateHubSpotDeal: createAsyncThunk(
    'campaignTask/queueCreateHubSpotDeal',
    async (payload: any, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(`/campaigns/${payload.campaign_id}/queue_create_hubspot_deal/`, payload);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response.data);
      }
    },
  ),
  queueUpdateHubSpotDeal: createAsyncThunk(
    'campaignTask/queueUpdateHubSpotDeal',
    async (payload: any, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(`/campaigns/${payload.campaign_id}/queue_update_hubspot_deal/`, payload);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response.data);
      }
    },
  ),
  queueCheckHubSpotProperties: createAsyncThunk(
    'campaignTask/queueCheckHubSpotProperties',
    async (payload: any, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(
          `/campaigns/${payload.campaign_id}/queue_check_hubspot_properties/`,
          payload,
        );
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response.data);
      }
    },
  ),
};
const initialState = {
  tasks: {as: 'idle'} as ListResult<CampaignTask>,
  runAnalysisInProgress: false,
};
export const campaignTaskSlice = createSlice({
  name: 'campaignTask',
  initialState: {
    ...initialState,
  } as IState,
  reducers: {
    clearTasks(state) {
      state.tasks = {as: 'idle'};
    },
  },
  extraReducers: builder => {
    builder
      .addCase(campaignTaskEndpoints.getCampaignTaskStatus.fulfilled, (state, {meta, payload}) => {
        updateTaskStatus(state, meta.arg, payload);
      })
      .addCase(campaignTaskEndpoints.runAnalysis.pending, state => {
        state.runAnalysisInProgress = true;
      })
      .addCase(campaignTaskEndpoints.runAnalysis.fulfilled, state => {
        state.runAnalysisInProgress = false;
      })
      .addCase(campaignTaskEndpoints.runAnalysis.rejected, state => {
        state.runAnalysisInProgress = false;
      })
      .addCase(campaignTaskEndpoints.refreshSalesMetrics.fulfilled, (state, {payload}) => {
        appendTask(state, {
          code: 'calc_sales_metrics',
          task_id: payload?.calc_sales_metrics_task?.id,
          status: 'IN_PROGRESS',
        });
        appendTask(state, {
          code: 'calc_upc_metrics',
          task_id: payload?.calc_upc_metrics_task?.id,
          status: 'IN_PROGRESS',
        });
        appendTask(state, {
          code: 'calc_sales_weekly_metrics',
          task_id: payload?.calc_sales_weekly_metrics_task?.id,
          status: 'IN_PROGRESS',
        });
      })
      .addCase(campaignTaskEndpoints.refreshStoreMetrics.fulfilled, (state, {payload}) => {
        appendTask(state, {code: 'ttest', task_id: payload?.ttest_task?.id, status: 'IN_PROGRESS'});
        appendTask(state, {
          code: 'calc_store_metrics',
          task_id: payload?.calc_store_metrics_task?.id,
          status: 'IN_PROGRESS',
        });
      })
      .addCase(campaignTaskEndpoints.computeSales.fulfilled, (state, {payload}) => {
        appendTask(state, {code: 'per_store_sales_calculations', task_id: payload?.task?.id, status: 'IN_PROGRESS'});
      })
      .addCase(campaignTaskEndpoints.computeItemSales.fulfilled, (state, {payload}) => {
        appendTask(state, {
          code: 'per_item_sales_calculations',
          task_id: payload?.task?.per_item_sales_calculations?.id,
          status: 'IN_PROGRESS',
        });
        appendTask(state, {
          code: 'calculate_campaign_weekly_item_metrics',
          task_id: payload?.task?.calculate_campaign_weekly_item_metrics?.id,
          status: 'IN_PROGRESS',
        });
      })
      .addCase(campaignTaskEndpoints.computeSalesReportData.fulfilled, (state, {payload}) => {
        appendTask(state, {
          code: 'calculate_sales_report_data',
          task_id: payload?.task?.id,
          status: 'IN_PROGRESS',
        });
      })
      .addCase(campaignTaskEndpoints.computeSalesReportDataRegional.fulfilled, (state, {payload}) => {
        appendTask(state, {
          code: 'calculate_sales_report_data_regional',
          task_id: payload?.task?.id,
          status: 'IN_PROGRESS',
        });
      })
      .addCase(campaignTaskEndpoints.computeStoreRollups.fulfilled, (state, {payload}) => {
        appendTask(state, {code: 'per_store_sales_rollup', task_id: payload?.task?.id, status: 'IN_PROGRESS'});
      })
      .addCase(storeEndpoints.updateTestControl.fulfilled, (state, {payload}) => {
        appendTask(state, {code: 'test_control', task_id: payload?.task?.id, status: 'IN_PROGRESS'});
      })
      .addCase(campaignTaskEndpoints.syncCampaignEntitiesFromSales.fulfilled, (state, {payload}) => {
        appendTask(state, {
          code: 'sync_campaign_entities_from_sales',
          task_id: payload?.task?.id,
          status: 'IN_PROGRESS',
        });
      })
      .addCase(navigationActions.setContextCampaign, () => {
        return {...initialState};
      })
      .addCase(campaignTaskEndpoints.startValassisReportGeneration.fulfilled, (state, {payload}) => {
        appendTask(state, {code: 'generate_valassis_config_files', task_id: payload?.task?.id, status: 'IN_PROGRESS'});
      })
      .addCase(campaignTaskEndpoints.importCampaignDataFile.fulfilled, (state, {payload}) => {
        appendTask(state, {
          code: payload?.task?.code,
          task_id: payload?.task?.task_id,
          status: 'IN_PROGRESS',
        });
      })
      .addCase(campaignTaskEndpoints.queueCreateHubSpotDeal.fulfilled, (state, {payload}) => {
        appendTask(state, {
          code: 'push_to_hubspot_task_create',
          task_id: payload?.task?.task_id,
          status: 'IN_PROGRESS',
        });
      })
      .addCase(campaignTaskEndpoints.queueUpdateHubSpotDeal.fulfilled, (state, {payload}) => {
        appendTask(state, {
          code: 'push_to_hubspot_task_update',
          task_id: payload?.task?.task_id,
          status: 'IN_PROGRESS',
        });
      })
      .addCase(campaignTaskEndpoints.queueCheckHubSpotProperties.fulfilled, (state, {payload}) => {
        appendTask(state, {
          code: 'check_hubspot_properties_task',
          task_id: payload?.task?.task_id,
          status: 'IN_PROGRESS',
        });
      });

    addResultCases(builder, campaignTaskEndpoints.getCampaignTasks, 'tasks');
  },
});

export const campaignTaskActions = campaignTaskSlice.actions;

export type CampaignTaskSlice = {
  [campaignTaskSlice.name]: ReturnType<(typeof campaignTaskSlice)['reducer']>;
};
